/**
 * Enhanced Error Boundary Component
 *
 * A comprehensive error boundary with:
 * - Multiple fallback component options
 * - Auto-reset on navigation (via resetKeys)
 * - Error logging callbacks
 * - Support for both render prop and component fallbacks
 * - Full RTL support and bilingual messaging
 *
 * Design: Arabic Heritage gold theme with accessible, user-friendly messaging
 */

import {
  Component,
  type ErrorInfo,
  type ReactNode,
  type ComponentType,
  useEffect,
  useCallback,
  type FC,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  RefreshCw,
  Home,
  WifiOff,
  FileQuestion,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  useReducedMotion,
  springPresets,
  scaleIn,
  fadeInUp,
} from '@/utils/animations';

// =============================================================================
// Types
// =============================================================================

/**
 * Props passed to fallback components
 */
export interface FallbackProps {
  /** The error that was caught */
  error: Error;
  /** Function to reset the error boundary */
  resetErrorBoundary: () => void;
}

/**
 * Props for the ErrorBoundary component
 */
export interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: ReactNode;
  /** Static fallback UI (simple ReactNode) */
  fallback?: ReactNode;
  /** Fallback component that receives error and reset function */
  FallbackComponent?: ComponentType<FallbackProps>;
  /** Render prop for custom fallback rendering */
  fallbackRender?: (props: FallbackProps) => ReactNode;
  /** Callback when error is caught (for logging/reporting) */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Callback when error boundary resets */
  onReset?: () => void;
  /** Keys that trigger auto-reset when changed (e.g., route path) */
  resetKeys?: unknown[];
}

/**
 * State for the ErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// =============================================================================
// Error Boundary Class Component
// =============================================================================

/**
 * ErrorBoundary Component
 *
 * A React error boundary that catches JavaScript errors anywhere in its
 * child component tree, logs those errors, and displays a fallback UI.
 *
 * Features:
 * - Catches rendering errors in child components
 * - Multiple fallback options (static, component, render prop)
 * - Auto-reset via resetKeys (useful for route changes)
 * - Error logging callback for analytics
 * - Full RTL support with bilingual messaging
 * - Accessible with proper ARIA attributes
 *
 * Usage:
 * ```tsx
 * // Simple usage with default fallback
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // With custom fallback component
 * <ErrorBoundary FallbackComponent={PageErrorFallback}>
 *   <MyPage />
 * </ErrorBoundary>
 *
 * // With render prop
 * <ErrorBoundary
 *   fallbackRender={({ error, resetErrorBoundary }) => (
 *     <CustomError error={error} onRetry={resetErrorBoundary} />
 *   )}
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // With auto-reset on route change
 * <ErrorBoundary resetKeys={[pathname]}>
 *   <Routes>...</Routes>
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call optional onError callback for logging/reporting
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Check if any resetKey has changed
    const { resetKeys } = this.props;
    const prevResetKeys = prevProps.resetKeys;

    if (this.state.hasError && resetKeys && prevResetKeys) {
      // Check if any key has changed
      const hasKeyChanged = resetKeys.some(
        (key, index) => key !== prevResetKeys[index]
      );

      if (hasKeyChanged) {
        this.reset();
      }
    }
  }

  reset = (): void => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback, FallbackComponent, fallbackRender } =
      this.props;

    if (hasError && error) {
      const fallbackProps: FallbackProps = {
        error,
        resetErrorBoundary: this.reset,
      };

      // Priority: fallbackRender > FallbackComponent > fallback > default
      if (fallbackRender) {
        return fallbackRender(fallbackProps);
      }

      if (FallbackComponent) {
        return <FallbackComponent {...fallbackProps} />;
      }

      if (fallback) {
        return fallback;
      }

      // Default fallback
      return <ErrorFallback onReset={this.reset} />;
    }

    return children;
  }
}

// =============================================================================
// Default Error Fallback
// =============================================================================

/**
 * Props for the ErrorFallback component
 */
export interface ErrorFallbackProps {
  /** Callback to reset the error state */
  onReset: () => void;
  /** Optional error message to display */
  error?: Error | null;
}

/**
 * ErrorFallback Component
 *
 * A bilingual error fallback UI displayed when an error is caught.
 * Can be used standalone or as the default fallback for ErrorBoundary.
 */
export const ErrorFallback: FC<ErrorFallbackProps> = ({ onReset }) => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();

  const MotionWrapper = prefersReducedMotion ? 'div' : motion.div;
  const motionProps = prefersReducedMotion
    ? {}
    : {
        initial: 'hidden',
        animate: 'visible',
        variants: scaleIn,
      };

  return (
    <MotionWrapper
      {...motionProps}
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center',
        'min-h-[300px] p-8',
        'text-center'
      )}
    >
      {/* Error Icon */}
      <div
        className={cn(
          'flex items-center justify-center',
          'w-16 h-16 rounded-full',
          'bg-error-100 text-error-600',
          'mb-6'
        )}
      >
        <AlertTriangle size={32} aria-hidden="true" />
      </div>

      {/* Error Title */}
      <h2 className="text-xl font-heading font-bold text-surface-900 mb-2">
        {t('errors.general')}
      </h2>

      {/* Error Description */}
      <p className="text-surface-600 mb-6 max-w-md">
        {t('errors.pageError.description')}
      </p>

      {/* Retry Button */}
      <button
        type="button"
        onClick={onReset}
        className={cn(
          'inline-flex items-center gap-2',
          'px-6 py-3 rounded-lg',
          'text-base font-medium',
          'bg-primary-600 text-white',
          'hover:bg-primary-700 active:bg-primary-800',
          'transition-colors duration-200',
          'touch-target',
          'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
        )}
      >
        <RefreshCw size={18} aria-hidden="true" />
        <span>{t('errors.tryAgain')}</span>
      </button>
    </MotionWrapper>
  );
};

// =============================================================================
// Page Error Fallback
// =============================================================================

export interface PageErrorFallbackProps extends FallbackProps {
  /** Optional custom title */
  title?: string;
  /** Optional custom description */
  description?: string;
}

/**
 * PageErrorFallback Component
 *
 * Full-page error fallback with illustration space, retry button,
 * and navigation to home. Designed for route-level errors.
 */
export const PageErrorFallback: FC<PageErrorFallbackProps> = ({
  resetErrorBoundary,
  title,
  description,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const handleGoHome = useCallback(() => {
    resetErrorBoundary();
    navigate('/');
  }, [navigate, resetErrorBoundary]);

  const MotionDiv = prefersReducedMotion ? 'div' : motion.div;
  const containerProps = prefersReducedMotion
    ? {}
    : {
        initial: 'hidden',
        animate: 'visible',
        variants: fadeInUp,
      };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-surface-50"
      data-testid="page-error-fallback"
    >
      <MotionDiv
        {...containerProps}
        role="alert"
        className="max-w-md w-full text-center"
      >
        {/* Error Icon with decorative ring */}
        <div className="relative mb-8 inline-block">
          <div
            className={cn(
              'w-24 h-24 rounded-full',
              'bg-error-100',
              'flex items-center justify-center',
              'mx-auto'
            )}
          >
            <AlertTriangle
              size={48}
              className="text-error-600"
              aria-hidden="true"
            />
          </div>
          {/* Decorative ring */}
          <div
            className={cn(
              'absolute inset-0 -m-2',
              'rounded-full border-2 border-error-200',
              'opacity-60'
            )}
            aria-hidden="true"
          />
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-surface-900 mb-4">
          {title || t('errors.pageError.title')}
        </h1>

        {/* Description */}
        <p className="text-surface-600 mb-8 leading-relaxed">
          {description || t('errors.pageError.description')}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={resetErrorBoundary}
            className={cn(
              'inline-flex items-center justify-center gap-2',
              'px-6 py-3 rounded-xl',
              'text-base font-medium',
              'bg-primary-600 text-white',
              'hover:bg-primary-700 active:bg-primary-800',
              'transition-colors duration-200',
              'shadow-md hover:shadow-lg',
              'touch-target',
              'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
            )}
            data-testid="page-error-retry"
          >
            <RefreshCw size={18} aria-hidden="true" />
            <span>{t('errors.pageError.tryAgain')}</span>
          </button>

          <button
            type="button"
            onClick={handleGoHome}
            className={cn(
              'inline-flex items-center justify-center gap-2',
              'px-6 py-3 rounded-xl',
              'text-base font-medium',
              'border border-surface-300 text-surface-700',
              'hover:bg-surface-50 hover:border-surface-400',
              'transition-colors duration-200',
              'touch-target',
              'focus-visible:ring-2 focus-visible:ring-surface-500 focus-visible:ring-offset-2'
            )}
            data-testid="page-error-home"
          >
            <Home size={18} aria-hidden="true" />
            <span>{t('errors.pageError.goHome')}</span>
          </button>
        </div>
      </MotionDiv>
    </div>
  );
};

// =============================================================================
// Section Error Fallback
// =============================================================================

export interface SectionErrorFallbackProps extends FallbackProps {
  /** Optional custom title */
  title?: string;
}

/**
 * SectionErrorFallback Component
 *
 * Inline section error with border accent. For errors within a page
 * that shouldn't block the entire interface.
 */
export const SectionErrorFallback: FC<SectionErrorFallbackProps> = ({
  resetErrorBoundary,
  title,
}) => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();

  const MotionDiv = prefersReducedMotion ? 'div' : motion.div;
  const motionProps = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, x: -8 },
        animate: { opacity: 1, x: 0 },
        transition: springPresets.gentle,
      };

  return (
    <MotionDiv
      {...motionProps}
      role="alert"
      className={cn(
        'flex items-center justify-between gap-4',
        'p-4 rounded-lg',
        'bg-error-50 border-s-4 border-error-500'
      )}
      data-testid="section-error-fallback"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle
          size={20}
          className="text-error-600 flex-shrink-0"
          aria-hidden="true"
        />
        <span className="text-sm font-medium text-error-800">
          {title || t('errors.sectionError.title')}
        </span>
      </div>

      <button
        type="button"
        onClick={resetErrorBoundary}
        className={cn(
          'inline-flex items-center gap-1.5',
          'px-3 py-1.5 rounded-lg',
          'text-sm font-medium',
          'bg-white text-error-700',
          'border border-error-200',
          'hover:bg-error-50 hover:border-error-300',
          'transition-colors duration-200',
          'focus-visible:ring-2 focus-visible:ring-error-500 focus-visible:ring-offset-2'
        )}
        data-testid="section-error-retry"
      >
        <RefreshCw size={14} aria-hidden="true" />
        <span>{t('errors.sectionError.retry')}</span>
      </button>
    </MotionDiv>
  );
};

// =============================================================================
// Card Error Fallback
// =============================================================================

export interface CardErrorFallbackProps extends FallbackProps {
  /** Height hint for the card placeholder */
  height?: string;
}

/**
 * CardErrorFallback Component
 *
 * Error state within a card container. Maintains the card's
 * expected dimensions while showing the error state.
 */
export const CardErrorFallback: FC<CardErrorFallbackProps> = ({
  resetErrorBoundary,
  height = 'h-48',
}) => {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'p-4 rounded-xl',
        'bg-surface-50 border border-surface-200',
        height
      )}
      data-testid="card-error-fallback"
    >
      <div className="w-10 h-10 rounded-full bg-error-100 flex items-center justify-center">
        <AlertTriangle size={20} className="text-error-500" aria-hidden="true" />
      </div>

      <span className="text-sm text-surface-600 text-center">
        {t('errors.sectionError.title')}
      </span>

      <button
        type="button"
        onClick={resetErrorBoundary}
        className={cn(
          'text-sm font-medium text-primary-600',
          'hover:text-primary-700 hover:underline',
          'transition-colors duration-200',
          'focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-primary-500 focus-visible:rounded'
        )}
        data-testid="card-error-retry"
      >
        {t('errors.sectionError.retry')}
      </button>
    </div>
  );
};

// =============================================================================
// Network Error Fallback
// =============================================================================

export interface NetworkErrorFallbackProps extends FallbackProps {
  /** Auto-retry interval in seconds (0 = no auto-retry) */
  autoRetrySeconds?: number;
}

/**
 * NetworkErrorFallback Component
 *
 * Specialized fallback for network/connection errors.
 * Features a distinct wifi icon and connectivity-focused messaging.
 */
export const NetworkErrorFallback: FC<NetworkErrorFallbackProps> = ({
  resetErrorBoundary,
  autoRetrySeconds = 0,
}) => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();

  // Auto-retry effect
  useEffect(() => {
    if (autoRetrySeconds > 0) {
      const timer = setTimeout(() => {
        resetErrorBoundary();
      }, autoRetrySeconds * 1000);

      return () => clearTimeout(timer);
    }
  }, [autoRetrySeconds, resetErrorBoundary]);

  const MotionDiv = prefersReducedMotion ? 'div' : motion.div;
  const motionProps = prefersReducedMotion
    ? {}
    : {
        initial: 'hidden',
        animate: 'visible',
        variants: scaleIn,
      };

  return (
    <MotionDiv
      {...motionProps}
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center',
        'min-h-[300px] p-8',
        'text-center'
      )}
      data-testid="network-error-fallback"
    >
      {/* Network Icon */}
      <div
        className={cn(
          'flex items-center justify-center',
          'w-16 h-16 rounded-full',
          'bg-info-100 text-info-600',
          'mb-6'
        )}
      >
        <WifiOff size={32} aria-hidden="true" />
      </div>

      {/* Title */}
      <h2 className="text-xl font-heading font-bold text-surface-900 mb-2">
        {t('errors.networkError.title')}
      </h2>

      {/* Description */}
      <p className="text-surface-600 mb-6 max-w-md">
        {t('errors.networkError.description')}
      </p>

      {/* Retry Button */}
      <button
        type="button"
        onClick={resetErrorBoundary}
        className={cn(
          'inline-flex items-center gap-2',
          'px-6 py-3 rounded-lg',
          'text-base font-medium',
          'bg-info-600 text-white',
          'hover:bg-info-700 active:bg-info-800',
          'transition-colors duration-200',
          'touch-target',
          'focus-visible:ring-2 focus-visible:ring-info-500 focus-visible:ring-offset-2'
        )}
        data-testid="network-error-retry"
      >
        <RefreshCw size={18} aria-hidden="true" />
        <span>{t('errors.tryAgain')}</span>
      </button>

      {autoRetrySeconds > 0 && (
        <p className="text-sm text-surface-500 mt-4">
          {t('errors.networkError.autoRetry', { seconds: autoRetrySeconds })}
        </p>
      )}
    </MotionDiv>
  );
};

// =============================================================================
// Not Found Page
// =============================================================================

/**
 * NotFoundPage Component
 *
 * A styled 404 page with Arabic Heritage gold theme.
 * Features navigation back and to home.
 */
export const NotFoundPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const MotionDiv = prefersReducedMotion ? 'div' : motion.div;
  const containerProps = prefersReducedMotion
    ? {}
    : {
        initial: 'hidden',
        animate: 'visible',
        variants: fadeInUp,
      };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-surface-50"
      data-testid="not-found-page"
    >
      <MotionDiv
        {...containerProps}
        className="max-w-md w-full text-center"
      >
        {/* 404 Number */}
        <div className="mb-6">
          <span
            className={cn(
              'text-[120px] md:text-[160px] font-heading font-bold',
              'text-primary-100 leading-none select-none',
              'block'
            )}
          >
            404
          </span>
        </div>

        {/* Icon */}
        <div className="relative mb-6 inline-block">
          <div
            className={cn(
              'w-20 h-20 rounded-full',
              'bg-accent-100',
              'flex items-center justify-center',
              'mx-auto'
            )}
          >
            <FileQuestion
              size={40}
              className="text-accent-600"
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-surface-900 mb-4">
          {t('errors.notFound.title')}
        </h1>

        {/* Description */}
        <p className="text-surface-600 mb-8 leading-relaxed">
          {t('errors.notFound.description')}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={handleGoHome}
            className={cn(
              'inline-flex items-center justify-center gap-2',
              'px-6 py-3 rounded-xl',
              'text-base font-medium',
              'bg-primary-600 text-white',
              'hover:bg-primary-700 active:bg-primary-800',
              'transition-colors duration-200',
              'shadow-md hover:shadow-lg',
              'touch-target',
              'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
            )}
            data-testid="not-found-home"
          >
            <Home size={18} aria-hidden="true" />
            <span>{t('errors.notFound.goHome')}</span>
          </button>

          <button
            type="button"
            onClick={handleGoBack}
            className={cn(
              'inline-flex items-center justify-center gap-2',
              'px-6 py-3 rounded-xl',
              'text-base font-medium',
              'border border-surface-300 text-surface-700',
              'hover:bg-surface-50 hover:border-surface-400',
              'transition-colors duration-200',
              'touch-target',
              'focus-visible:ring-2 focus-visible:ring-surface-500 focus-visible:ring-offset-2'
            )}
            data-testid="not-found-back"
          >
            <ArrowLeft
              size={18}
              className="rtl:rotate-180"
              aria-hidden="true"
            />
            <span>{t('errors.notFound.goBack')}</span>
          </button>
        </div>
      </MotionDiv>
    </div>
  );
};

// =============================================================================
// withErrorBoundary HOC
// =============================================================================

export interface WithErrorBoundaryOptions {
  /** Fallback component to show on error */
  FallbackComponent?: ComponentType<FallbackProps>;
  /** Error callback for logging */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Reset callback */
  onReset?: () => void;
}

/**
 * Higher-order component that wraps a component with an ErrorBoundary
 *
 * @example
 * ```tsx
 * const SafeMyComponent = withErrorBoundary(MyComponent, {
 *   FallbackComponent: SectionErrorFallback,
 *   onError: (error) => logError(error),
 * });
 * ```
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: ComponentType<P>,
  options: WithErrorBoundaryOptions = {}
): FC<P> {
  const { FallbackComponent, onError, onReset } = options;

  const WithErrorBoundaryComponent: FC<P> = (props) => {
    return (
      <ErrorBoundary
        FallbackComponent={FallbackComponent}
        onError={onError}
        onReset={onReset}
      >
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };

  // Set display name for debugging
  const wrappedName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${wrappedName})`;

  return WithErrorBoundaryComponent;
}

// =============================================================================
// Route Error Boundary Hook
// =============================================================================

/**
 * Hook to create route-aware error boundary props
 *
 * Provides resetKeys based on current location for auto-reset on navigation.
 *
 * @example
 * ```tsx
 * const errorBoundaryProps = useRouteErrorBoundary();
 * return <ErrorBoundary {...errorBoundaryProps}>{children}</ErrorBoundary>;
 * ```
 */
export function useRouteErrorBoundary(): Pick<
  ErrorBoundaryProps,
  'resetKeys' | 'FallbackComponent'
> {
  const location = useLocation();

  return {
    resetKeys: [location.pathname],
    FallbackComponent: PageErrorFallback,
  };
}

// =============================================================================
// Exports
// =============================================================================

export default ErrorBoundary;
